import { Component, Input, OnInit } from '@angular/core';
import { HcToastrService } from '@manager-app/service/toastr.service';
import { ErrorHandlerService } from '@manager-app/service/error-handler.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { HxAuthService, HxInvoiceService, HxStoreService, HxUiLabelPipe, isoDate, ProductInfoType, StoreBasicModel } from 'hx-services';
import { StoreProductItem } from '@manager-app/components/store-products-search/store-products-search.component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { HxStoreSelectComponent } from 'hx-component';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { NgFor, NgIf } from '@angular/common';
import { StoreProductsSearchComponent } from '../../components/store-products-search/store-products-search.component';

export type InvoiceMode = 'EXPORT' | 'RESTORATION';

@Component({
    selector: 'app-balance-new.m-grid__item.m-grid__item--fluid',
    templateUrl: './balance-new.component.html',
    styleUrls: ['./balance-new.component.css'],
  imports: [FormsModule, ReactiveFormsModule, HxStoreSelectComponent, NgSelectComponent, NgFor, StoreProductsSearchComponent, NgIf, TranslocoPipe, HxUiLabelPipe, NgOptionComponent]
})
export class BalanceNewComponent implements OnInit {
  @Input() storeId?: number;
  invoiceFormKeys = {
    fromStoreId: 'fromStoreId',
    toStoreId: 'toStoreId',
    description: 'description',
    products: 'products'
  };
  productFormKeys = {
    productInfoId: 'productInfoId',
    amount: 'amount'
  };
  isLoading = {
    submit: false,
  };
  invoiceForm!: UntypedFormGroup;
  productInfoTypes: ProductInfoType[] = [ProductInfoType.PROPERTY, ProductInfoType.PRODUCT, ProductInfoType.MATERIAL_ASSETS, ProductInfoType.NON_MATERIAL_ASSETS];
  stores?: StoreBasicModel[];
  mode: InvoiceMode = 'EXPORT';
  private allStores: StoreBasicModel[] = [];
  private currentStore?: StoreBasicModel;

  constructor(
    private toastr: HcToastrService,
    private errorService: ErrorHandlerService,
    private modal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private auth: HxAuthService,
    private storeService: HxStoreService,
    private invoiceService: HxInvoiceService,
    private tr: TranslocoService,
  ) {
  }

  async ngOnInit() {
    this.createForm();
    if (this.storeId) {
      const store = await this.storeService.getFullStore(this.storeId);
      this.onFromStoreChanged(store);
    }
  }

  dismissModal() {
    this.modal.dismiss();
  }

  onSubmitForm() {
    // eslint-disable-next-line guard-for-in
    for (const i in this.invoiceForm.controls) {
      this.invoiceForm.controls[i].markAsDirty();
      this.invoiceForm.controls[i].updateValueAndValidity();
    }
    if (this.invoiceForm.valid) {
      this.invoiceForm.disable();

      this.isLoading.submit = true;
      const obj = { ...this.invoiceForm.getRawValue(), restoration: this.mode === 'RESTORATION' };
      this.invoiceService.createInvoice(obj).subscribe(() => {
        this.invoiceForm.enable();
        this.toastr.success(this.tr.translate('balance-new.ts.invoiceSuccess'));
        this.isLoading.submit = false;
        this.modal.close();
      }, err => {
        this.invoiceForm.enable();
        this.isLoading.submit = false;
        this.errorService.handler(err.error);
      });
    }
  }

  addItem() {
    this.asFormArray(this.invoiceFormKeys.products).push(this.createItem());
  }

  removeItem(index: number) {
    this.asFormArray(this.invoiceFormKeys.products).removeAt(index);
  }

  asFormArray(path: string): UntypedFormArray {
    return this.invoiceForm.get(path) as UntypedFormArray;
  }

  onStoreProductSelected(item: StoreProductItem, index: number): void {
    this.asFormArray(this.invoiceFormKeys.products).at(index).patchValue({productInfoId: item.productInfoId});
  }

  onFromStoreChanged(store?: StoreBasicModel | StoreBasicModel[]) {
    if (store && !Array.isArray(store)) {
      this.currentStore = store;
      this.storeService.getOwnerAllStores(store.id).subscribe(stores => {
        this.allStores = stores;
        this.applyToStoreFilter();
      });
      this.productInfoTypesChange(store);
    } else if (!store && this.invoiceForm.get(this.invoiceFormKeys.toStoreId)?.value) {
      this.stores = [];
      this.currentStore = undefined;
      this.allStores = [];
      this.invoiceForm.get(this.invoiceFormKeys.toStoreId)?.setValue(null);
    }
  }

  onModeChanged(mode: InvoiceMode) {
    this.mode = mode;
    this.invoiceForm.get(this.invoiceFormKeys.toStoreId)?.setValue(null);
    this.applyToStoreFilter();
  }

  private applyToStoreFilter() {
    if (!this.currentStore) {
      this.stores = [];
      return;
    }
    const activeStores = this.allStores.filter(r => r.endWorkDate > isoDate(Date.now()));
    if (this.mode === 'RESTORATION') {
      this.stores = activeStores;
    } else {
      this.stores = activeStores.filter(r => r.id !== this.currentStore!.id);
    }
  }

  private createForm(): void {
    this.invoiceForm = this.fb.group({
      fromStoreId: [this.storeId, [Validators.required]],
      toStoreId: [null, [Validators.required]],
      description: [null],
      products: this.fb.array([], [Validators.required]),
    });
  }

  private createItem() {
    return this.fb.group({
      productInfoId: [null, [Validators.required]],
      amount: [null, [Validators.required, Validators.min(0)]],
    });
  }

  private productInfoTypesChange(store?: StoreBasicModel) {
    if (store && store.cityId === 5) {
      this.productInfoTypes.push(ProductInfoType.INGREDIENT);
    } else {
      if (this.productInfoTypes.includes(ProductInfoType.INGREDIENT)) {
        this.productInfoTypes.splice(this.productInfoTypes.indexOf(ProductInfoType.INGREDIENT), 1);
      }
    }
  }
}
